import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { productsApi } from '../../api/products.js';
import { customizationsApi } from '../../api/customizations.js';
import {
  useProducts,
  useCreateProduct,
  useUpdateProduct,
  useImportProducts,
} from '../../hooks/useProducts.js';

const DEBOUNCE_MS = 350;
const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

/* ── Skeleton rows ── */
const SkeletonRows = ({ n = 8 }) => (
  <>
    {Array.from({ length: n }).map((_, i) => (
      <tr key={i}>
        {Array.from({ length: 11 }).map((__, j) => (
          <td key={j}><div style={{ height: 14, borderRadius: 4, background: 'linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.2s infinite', width: j === 1 ? '70%' : '50%' }} /></td>
        ))}
      </tr>
    ))}
  </>
);

/* ══════════════════════════════════════════
   ADD PRODUCT MODAL
══════════════════════════════════════════ */
const AddProductModal = ({ categories, onClose }) => {
  const [form, setForm] = useState({ name: '', sku: '', min_price: '', making_cost: '', category_id: '', hsn_code: '' });
  const [errors, setErrors] = useState({});
  const nameRef = useRef(null);
  useEffect(() => { nameRef.current?.focus(); }, []);

  const createProduct = useCreateProduct();
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Product name is required.';
    const p = parseFloat(form.min_price);
    if (form.min_price === '' || isNaN(p) || p < 0) e.min_price = 'Enter a valid non-negative price.';
    if (form.making_cost !== '') {
      const c = parseFloat(form.making_cost);
      if (isNaN(c) || c < 0) e.making_cost = 'Enter a valid non-negative amount.';
    }
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    try {
      await createProduct.mutateAsync({
        name: form.name.trim(),
        ...(form.sku.trim() ? { sku: form.sku.trim() } : {}),
        min_price: parseFloat(form.min_price),
        making_cost: form.making_cost !== '' ? parseFloat(form.making_cost) : 0,
        category_id: form.category_id !== '' ? parseInt(form.category_id, 10) : null,
        hsn_code: form.hsn_code.trim() || null,
      });
      toast.success('Product created successfully!');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create product.');
    }
  };

  const saving = createProduct.isPending;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span style={{ fontWeight: 700 }}>Add New Product</span>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Product Name *</label>
              <input ref={nameRef} className={`form-control${errors.name ? ' is-invalid' : ''}`} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. 80MM X P3" />
              {errors.name && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.name}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">SKU <span style={{ color: '#94a3b8', fontWeight: 400 }}>(auto-generated if blank)</span></label>
              <input className="form-control" style={{ fontFamily: 'monospace' }} value={form.sku} onChange={e => set('sku', e.target.value.toUpperCase())} placeholder="e.g. SKU-80MM-X-P3" />
            </div>
            <div className="form-group">
              <label className="form-label">Price *</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600, color: '#475569' }}>₹</span>
                <input className={`form-control${errors.min_price ? ' is-invalid' : ''}`} type="number" min="0" step="0.01" value={form.min_price} onChange={e => set('min_price', e.target.value)} placeholder="0.00" />
              </div>
              {errors.min_price && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.min_price}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Making Cost <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600, color: '#475569' }}>₹</span>
                <input className={`form-control${errors.making_cost ? ' is-invalid' : ''}`} type="number" min="0" step="0.01" value={form.making_cost} onChange={e => set('making_cost', e.target.value)} placeholder="0.00" />
              </div>
              {errors.making_cost && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.making_cost}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select
                className="form-control"
                value={form.category_id}
                onChange={e => {
                  const catId = e.target.value;
                  const cat = (categories || []).find(c => String(c.id) === catId);
                  set('category_id', catId);
                  if (cat?.hsn_code) set('hsn_code', cat.hsn_code);
                }}
              >
                <option value="">— No Category —</option>
                {(categories || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">HSN Code</label>
              <input className="form-control" style={{ fontFamily: 'monospace' }} value={form.hsn_code} onChange={e => set('hsn_code', e.target.value)} placeholder="e.g. 48119099" />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? 'Saving…' : 'Add Product'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   EDIT PRODUCT MODAL
══════════════════════════════════════════ */
const EditProductModal = ({ product, categories, onClose }) => {
  const [form, setForm] = useState({ name: product.name, sku: product.sku, min_price: product.price, making_cost: product.makingCost ?? 0, is_active: product.isActive, category_id: product.categoryId ?? '', hsn_code: product.hsnCode ?? '' });
  const [errors, setErrors] = useState({});
  const nameRef = useRef(null);
  useEffect(() => { nameRef.current?.focus(); }, []);

  const updateProduct = useUpdateProduct();
  const setField = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required.';
    const p = parseFloat(form.min_price);
    if (form.min_price === '' || isNaN(p) || p < 0) e.min_price = 'Enter a valid non-negative price.';
    const c = parseFloat(form.making_cost);
    if (isNaN(c) || c < 0) e.making_cost = 'Enter a valid non-negative amount.';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    try {
      await updateProduct.mutateAsync({
        id: product.id,
        payload: {
          name: form.name.trim(),
          sku: form.sku.trim() || undefined,
          min_price: parseFloat(form.min_price),
          making_cost: parseFloat(form.making_cost),
          is_active: form.is_active,
          category_id: form.category_id !== '' ? parseInt(form.category_id, 10) : null,
          hsn_code: form.hsn_code.trim() || null,
        },
      });
      toast.success('Product updated successfully!');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update product.');
    }
  };

  const saving = updateProduct.isPending;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span style={{ fontWeight: 700 }}>Edit Product</span>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input ref={nameRef} className={`form-control${errors.name ? ' is-invalid' : ''}`} value={form.name} onChange={e => setField('name', e.target.value)} />
              {errors.name && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.name}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">SKU</label>
              <input className="form-control" style={{ fontFamily: 'monospace' }} value={form.sku} onChange={e => setField('sku', e.target.value.toUpperCase())} />
            </div>
            <div className="form-group">
              <label className="form-label">Price *</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600, color: '#475569' }}>₹</span>
                <input className={`form-control${errors.min_price ? ' is-invalid' : ''}`} type="number" min="0" step="0.01" value={form.min_price} onChange={e => setField('min_price', e.target.value)} />
              </div>
              {errors.min_price && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.min_price}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Making Cost</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600, color: '#475569' }}>₹</span>
                <input className={`form-control${errors.making_cost ? ' is-invalid' : ''}`} type="number" min="0" step="0.01" value={form.making_cost} onChange={e => setField('making_cost', e.target.value)} />
              </div>
              {errors.making_cost && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.making_cost}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select
                className="form-control"
                value={form.category_id}
                onChange={e => {
                  const catId = e.target.value;
                  const cat = (categories || []).find(c => String(c.id) === catId);
                  setField('category_id', catId);
                  if (cat?.hsn_code) setField('hsn_code', cat.hsn_code);
                }}
              >
                <option value="">— No Category —</option>
                {(categories || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">HSN Code</label>
              <input className="form-control" style={{ fontFamily: 'monospace' }} value={form.hsn_code} onChange={e => setField('hsn_code', e.target.value)} placeholder="e.g. 48119099" />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 500 }}>
                <input type="checkbox" checked={form.is_active} onChange={e => setField('is_active', e.target.checked)} style={{ width: 15, height: 15 }} />
                Product is Active
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? 'Saving…' : 'Save Product'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   IMPORT MODAL
══════════════════════════════════════════ */
const ImportModal = ({ onClose }) => {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const importProducts = useImportProducts();

  const handleDownloadTemplate = async () => {
    try {
      const res = await productsApi.downloadTemplate();
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a'); a.href = url; a.download = 'products_import_template.csv'; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) { setFile(f); setResult(null); } };
  const handleFileChange = (e) => { const f = e.target.files[0]; if (f) { setFile(f); setResult(null); } e.target.value = ''; };

  const handleImport = async () => {
    if (!file) return;
    setResult(null);
    try {
      const text = await file.text();
      const res = await importProducts.mutateAsync(text);
      setResult(res);
      if (res.success) toast.success(`Import complete: ${res.summary.created} created, ${res.summary.updated} updated`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Import failed.');
    }
  };

  const importing = importProducts.isPending;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span style={{ fontWeight: 700 }}>Import Products</span>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <button className="btn btn-outline btn-sm" style={{ marginBottom: 16 }} onClick={handleDownloadTemplate} type="button">↓ Download CSV Template</button>

          <div
            style={{ border: `2px dashed ${dragOver ? '#6366f1' : '#cbd5e1'}`, borderRadius: 8, padding: 24, textAlign: 'center', cursor: importing ? 'default' : 'pointer', background: file ? '#f8faff' : '#fafafa', transition: 'border-color .15s' }}
            onClick={() => !importing && fileRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            <input ref={fileRef} type="file" accept=".csv,.txt" style={{ display: 'none' }} onChange={handleFileChange} />
            {file ? (
              <>
                <div style={{ fontSize: 28, marginBottom: 4 }}>📄</div>
                <p style={{ fontWeight: 600, margin: '0 0 4px' }}>{file.name}</p>
                <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>{(file.size / 1024).toFixed(1)} KB · click to change</p>
              </>
            ) : (
              <>
                <div style={{ fontSize: 28, marginBottom: 4 }}>📤</div>
                <p style={{ fontWeight: 600, margin: '0 0 4px' }}>Drop a CSV file here or <span style={{ color: '#6366f1', textDecoration: 'underline' }}>browse</span></p>
                <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Accepts .csv and .txt</p>
              </>
            )}
          </div>

          {result && (
            <div style={{ marginTop: 16, padding: '10px 14px', borderRadius: 6, background: '#f0fdf4', border: '1px solid #86efac' }}>
              <div style={{ display: 'flex', gap: 16, fontWeight: 600, fontSize: 13 }}>
                <span style={{ color: '#16a34a' }}>✓ {result.summary?.created ?? 0} created</span>
                <span style={{ color: '#2563eb' }}>✎ {result.summary?.updated ?? 0} updated</span>
                {result.summary?.failed > 0 && <span style={{ color: '#dc2626' }}>✕ {result.summary.failed} failed</span>}
              </div>
              {result.errors?.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#dc2626' }}>
                  {result.errors.map((e, i) => <div key={i}>• {typeof e === 'string' ? e : JSON.stringify(e)}</div>)}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline btn-sm" onClick={onClose} disabled={importing}>{result ? 'Close' : 'Cancel'}</button>
          <button type="button" className="btn btn-primary btn-sm" onClick={handleImport} disabled={!file || importing}>{importing ? 'Importing…' : '↑ Import'}</button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   EXPORT MODAL
══════════════════════════════════════════ */
const toDateInput = d => d.toISOString().slice(0, 10);
const defaultFrom = () => { const d = new Date(); d.setMonth(d.getMonth() - 1); return toDateInput(d); };
const defaultTo = () => toDateInput(new Date());

const ExportModal = ({ onClose }) => {
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [exporting, setExporting] = useState(false);
  const maxDate = toDateInput(new Date());

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const res = await productsApi.exportProducts(params);
      const disposition = res.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/);
      const filename = match?.[1] || `products_${from}_to_${to}.xlsx`;
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
      toast.success('Export downloaded!');
      onClose();
    } catch { toast.error('Export failed. Please try again.'); }
    finally { setExporting(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span style={{ fontWeight: 700 }}>Export Products</span>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>Download all products as an Excel file. Optionally narrow by date added.</p>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">From</label>
              <input type="date" className="form-control" value={from} max={to || maxDate} onChange={e => setFrom(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">To</label>
              <input type="date" className="form-control" value={to} min={from || undefined} max={maxDate} onChange={e => setTo(e.target.value)} />
            </div>
          </div>
          <p style={{ color: '#94a3b8', fontSize: 12 }}>Leave both dates blank to export all products.</p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline btn-sm" onClick={onClose} disabled={exporting}>Cancel</button>
          <button type="button" className="btn btn-primary btn-sm" onClick={handleExport} disabled={exporting}>{exporting ? 'Exporting…' : '↓ Export Excel'}</button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   MANAGE CUSTOMIZATIONS MODAL
══════════════════════════════════════════ */
const ManageCustomizationsModal = ({ product, onClose }) => {
  const [assignments, setAssignments] = useState([]);
  const [available, setAvailable] = useState([]);
  const [selId, setSelId] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchAssignments = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await customizationsApi.getByProduct(product.id);
      if (res?.success) setAssignments(res.data || []);
    } catch { toast.error('Failed to load assigned customizations.'); }
    finally { setLoading(false); }
  }, [product.id]);

  const fetchAvailable = React.useCallback(async () => {
    try {
      const res = await customizationsApi.getAll({ limit: 100 });
      if (res?.success) setAvailable(res.data || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchAssignments(); fetchAvailable(); }, [fetchAssignments, fetchAvailable]);

  const handleLink = async (e) => {
    e.preventDefault();
    if (!selId) { toast.error('Please select a customization.'); return; }
    setSaving(true);
    try {
      await customizationsApi.assign(product.id, { customization_id: parseInt(selId, 10), sort_order: parseInt(sortOrder, 10) || 0 });
      toast.success('Customization linked!');
      setSelId(''); setSortOrder(0); fetchAssignments();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to link.'); }
    finally { setSaving(false); }
  };

  const handleRemove = async (assignmentId) => {
    if (!window.confirm('Remove this customization from the product?')) return;
    try {
      await customizationsApi.removeAssignment(product.id, assignmentId);
      toast.success('Removed.'); fetchAssignments();
    } catch { toast.error('Failed to remove.'); }
  };

  const filteredAvailable = available.filter(a => !assignments.some(s => s.customization?.id === a.id));

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span style={{ fontWeight: 700 }}>Customizations — {product.name}</span>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ maxHeight: 460, overflowY: 'auto' }}>
          <p style={{ fontWeight: 700, marginBottom: 10 }}>Linked Customizations ({assignments.length})</p>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8' }}>Loading…</div>
          ) : assignments.length === 0 ? (
            <p style={{ color: '#94a3b8', fontSize: 13, padding: '16px 0' }}>No customizations linked to this product yet.</p>
          ) : (
            <div className="table-wrapper" style={{ marginBottom: 20 }}>
              <table>
                <thead><tr><th>Sort</th><th>Label</th><th>Type</th><th>Required</th><th></th></tr></thead>
                <tbody>
                  {assignments.map(a => (
                    <tr key={a.assignment_id}>
                      <td>{a.sort_order}</td>
                      <td style={{ fontWeight: 600 }}>{a.customization?.label}</td>
                      <td>{a.customization?.input_type}</td>
                      <td><span className={`badge ${a.customization?.is_required ? 'badge-danger' : 'badge-success'}`}>{a.customization?.is_required ? 'Mandatory' : 'Optional'}</span></td>
                      <td><button className="btn btn-xs" style={{ color: '#dc2626' }} onClick={() => handleRemove(a.assignment_id)}>✕ Remove</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {filteredAvailable.length > 0 && (
            <form onSubmit={handleLink} style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
              <p style={{ fontWeight: 700, marginBottom: 10 }}>Link Customization Standard</p>
              <div className="grid-2">
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Select Customization</label>
                  <select className="form-control" value={selId} onChange={e => setSelId(e.target.value)}>
                    <option value="">— Select —</option>
                    {filteredAvailable.map(a => <option key={a.id} value={a.id}>{a.label} ({a.input_type})</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Sort Order</label>
                  <input type="number" className="form-control" value={sortOrder} onChange={e => setSortOrder(e.target.value)} />
                </div>
              </div>
              <button type="submit" className="btn btn-primary btn-sm" style={{ marginTop: 10, width: '100%', justifyContent: 'center' }} disabled={saving || !selId}>
                {saving ? 'Linking…' : '+ Link Customization'}
              </button>
            </form>
          )}
          {available.length > 0 && filteredAvailable.length === 0 && (
            <p style={{ color: '#94a3b8', fontSize: 13, borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>All available customizations are already linked.</p>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   MANAGE CATEGORIES MODAL
══════════════════════════════════════════ */
const ManageCategoriesModal = ({ categories, onClose, onSaved }) => {
  const [editTarget, setEditTarget] = useState(null); // { id?, name, hsn_code }
  const [form, setForm] = useState({ name: '', hsn_code: '' });
  const [saving, setSaving] = useState(false);
  const nameRef = useRef(null);

  const openNew = () => { setForm({ name: '', hsn_code: '' }); setEditTarget({ isNew: true }); setTimeout(() => nameRef.current?.focus(), 50); };
  const openEdit = (c) => { setForm({ name: c.name, hsn_code: c.hsn_code || '' }); setEditTarget(c); setTimeout(() => nameRef.current?.focus(), 50); };
  const cancel = () => setEditTarget(null);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), hsn_code: form.hsn_code.trim() || null };
      if (editTarget.isNew) {
        await productsApi.createCategory(payload);
        toast.success('Category created!');
      } else {
        await productsApi.updateCategory(editTarget.id, payload);
        toast.success('Category updated!');
      }
      setEditTarget(null);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save category.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span style={{ fontWeight: 700 }}>Manage Categories</span>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>Name</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>HSN</th>
                <th style={{ width: 50 }}></th>
              </tr>
            </thead>
            <tbody>
              {categories.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px', fontWeight: 600, fontSize: 13 }}>{c.name}</td>
                  <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: 12, color: '#64748b' }}>{c.hsn_code || '—'}</td>
                  <td style={{ padding: '8px' }}>
                    <button className="btn btn-xs btn-outline" onClick={() => openEdit(c)}>✎</button>
                  </td>
                </tr>
              ))}
              {categories.length === 0 && (
                <tr><td colSpan={3} style={{ padding: 16, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No categories yet</td></tr>
              )}
            </tbody>
          </table>

          {editTarget ? (
            <form onSubmit={handleSave} style={{ background: '#f8fafc', borderRadius: 8, padding: 14, border: '1px solid #e2e8f0' }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: '#475569' }}>
                {editTarget.isNew ? 'Add New Category' : `Edit: ${editTarget.name}`}
              </div>
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label className="form-label">Name *</label>
                <input ref={nameRef} className="form-control" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. THERMAL PAPER" />
              </div>
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label className="form-label">HSN Code</label>
                <input className="form-control" style={{ fontFamily: 'monospace' }} value={form.hsn_code} onChange={e => setForm(f => ({ ...f, hsn_code: e.target.value }))} placeholder="e.g. 48119099" />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving || !form.name.trim()}>{saving ? 'Saving…' : 'Save'}</button>
                <button type="button" className="btn btn-outline btn-sm" onClick={cancel}>Cancel</button>
              </div>
            </form>
          ) : (
            <button className="btn btn-primary btn-sm" onClick={openNew}>+ Add Category</button>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════ */
export default function ProductsPage() {
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';

  // UI / filter state only — no data state
  const [search, setSearch] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('ASC');
  const [limit, setLimit] = useState(20);
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showCategories, setShowCategories] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [custTarget, setCustTarget] = useState(null);

  useEffect(() => {
    productsApi.getCategories().then(res => setCategories(res.data || [])).catch(() => {});
  }, []);

  // Debounced search — only updates the query key after the user stops typing
  const timer = useRef(null);
  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { setDebouncedSearch(val); setPage(1); }, DEBOUNCE_MS);
  };

  // useMemo keeps params reference stable — React Query only refetches when values actually change
  const params = useMemo(() => ({
    page,
    limit: limit === 100000 ? 100000 : limit,
    sortBy,
    sortOrder,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(categoryFilter ? { category_id: categoryFilter } : {}),
  }), [page, limit, sortBy, sortOrder, debouncedSearch, categoryFilter]);

  // Single query — table rows, total count, and pagination all read from data.*
  // keepPreviousData (set in useProducts) means old data stays visible while the next page loads
  const { data, isFetching: loading, refetch } = useProducts(params);

  const rawRows = data?.data || [];
  const totalCount = data?.count || 0;
  const totalPages = data?.totalPages || 1;

  const products = rawRows.map(p => ({
    id: p.id,
    name: p.name || '',
    sku: p.sku || '',
    price: parseFloat(p.min_price || 0),
    makingCost: parseFloat(p.making_cost || 0),
    isActive: p.is_active !== false,
    createdAt: fmt(p.created_at),
    updatedAt: fmt(p.updated_at),
    categoryId: p.category_id ?? null,
    categoryName: p.category?.name || '',
    hsnCode: p.hsn_code || '',
  }));

  const toggleSort = (col) => {
    if (sortBy === col) setSortOrder(o => o === 'ASC' ? 'DESC' : 'ASC');
    else { setSortBy(col); setSortOrder('ASC'); }
    setPage(1);
  };

  const startIdx = (page - 1) * (limit === 100000 ? totalCount : limit) + 1;
  const endIdx = Math.min(page * (limit === 100000 ? totalCount : limit), totalCount);

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <span style={{ opacity: 0.35, fontSize: 11 }}>⇅</span>;
    return <span style={{ fontSize: 11, color: '#6366f1' }}>{sortOrder === 'ASC' ? '↑' : '↓'}</span>;
  };

  return (
    <div className="page-body">
      <div className="card">
        <div className="card-header">
          <div>
            <span className="card-title">Product Catalog</span>
            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>
              {loading ? 'Loading…' : `${totalCount > 0 ? `${startIdx}–${endIdx} of ` : ''}${totalCount.toLocaleString()} product${totalCount !== 1 ? 's' : ''}`}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }}>🔍</span>
              <input
                style={{ paddingLeft: 32, paddingRight: search ? 28 : 10 }}
                className="form-control"
                value={search}
                onChange={e => handleSearch(e.target.value)}
                placeholder="Search by name or SKU…"
              />
              {search && <button onClick={() => handleSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 0, lineHeight: 1 }}>✕</button>}
            </div>
<button className="btn btn-outline btn-sm" onClick={() => setShowExport(true)}>↓ Export</button>
            <button className="btn btn-outline btn-sm" onClick={() => setShowImport(true)}>↑ Import</button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>+ Add Product</button>
          </div>
        </div>

        {/* Sort + Filter toolbar */}
        <div style={{ padding: '10px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>Sort by</span>
          {[{ col: 'name', label: 'Name' }, { col: 'min_price', label: 'Price' }, { col: 'created_at', label: 'Newest' }].map(({ col, label }) => (
            <button key={col}
              onClick={() => toggleSort(col)}
              style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 6, border: sortBy === col ? '1px solid #6366f1' : '1px solid #e2e8f0', background: sortBy === col ? '#eef2ff' : 'transparent', color: sortBy === col ? '#6366f1' : '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              {label} <SortIcon col={col} />
            </button>
          ))}
          {categories.length > 0 && (
            <select
              value={categoryFilter}
              onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}
              style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 6, border: categoryFilter ? '1px solid #6366f1' : '1px solid #e2e8f0', background: categoryFilter ? '#eef2ff' : 'transparent', color: categoryFilter ? '#6366f1' : '#64748b', cursor: 'pointer', height: 30 }}
            >
              <option value="">All Categories</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <button onClick={() => refetch()} disabled={loading} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', fontSize: 16 }} title="Refresh">↺</button>
        </div>

        <div className="card-body" style={{ padding: 0 }}>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 44 }}>#</th>
                  <th onClick={() => toggleSort('name')} style={{ cursor: 'pointer' }}>Product Name <SortIcon col="name" /></th>
                  <th>SKU</th>
                  <th>Category</th>
                  <th>HSN</th>
                  <th onClick={() => toggleSort('min_price')} style={{ cursor: 'pointer' }}>Price <SortIcon col="min_price" /></th>
                  <th onClick={() => toggleSort('making_cost')} style={{ cursor: 'pointer' }}>Making Cost <SortIcon col="making_cost" /></th>
                  <th>Status</th>
                  <th onClick={() => toggleSort('created_at')} style={{ cursor: 'pointer' }}>Added <SortIcon col="created_at" /></th>
                  <th>Last Updated</th>
                  <th style={{ width: 80 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <SkeletonRows n={8} />
                ) : products.length === 0 ? (
                  <tr>
                    <td colSpan={11} style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8' }}>
                      <div style={{ fontSize: 36, marginBottom: 8 }}>📦</div>
                      <p style={{ fontWeight: 600, margin: '0 0 4px' }}>No products found</p>
                      {debouncedSearch && <p style={{ fontSize: 13, margin: '0 0 12px' }}>No results for "{debouncedSearch}"</p>}
                      <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>+ Add First Product</button>
                    </td>
                  </tr>
                ) : (
                  products.map((p, i) => (
                    <tr key={p.id}>
                      <td style={{ color: '#94a3b8', fontSize: 12 }}>{startIdx + i}</td>
                      <td style={{ fontWeight: 600 }}>{p.name}</td>
                      <td><span style={{ fontFamily: 'monospace', fontSize: 12, background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>{p.sku || '—'}</span></td>
                      <td>
                        {p.categoryName
                          ? <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: '#f1f5f9', color: '#475569', whiteSpace: 'nowrap' }}>{p.categoryName}</span>
                          : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td><span style={{ fontFamily: 'monospace', fontSize: 12, color: '#64748b' }}>{p.hsnCode || '—'}</span></td>
                      <td style={{ fontWeight: 600 }}>₹{p.price.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td style={{ fontWeight: 600 }}>₹{p.makingCost.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td>
                        {p.isActive
                          ? <span className="badge" style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}>Active</span>
                          : <span className="badge badge-danger">Inactive</span>}
                      </td>
                      <td style={{ color: '#64748b', fontSize: 13 }}>{p.createdAt}</td>
                      <td style={{ color: '#64748b', fontSize: 13 }}>{p.updatedAt}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-xs btn-outline" title="Edit" onClick={() => setEditTarget(p)}>✎</button>
                          <button className="btn btn-xs" title="Manage Customizations" style={{ background: '#eef2ff', color: '#6366f1', border: '1px solid #c7d2fe' }} onClick={() => setCustTarget(p)}>⚙</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '16px 20px', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>Show:</span>
              <select
                className="form-control"
                style={{ width: 'auto', padding: '4px 8px', fontSize: 13, cursor: 'pointer', height: 'auto' }}
                value={limit === 100000 ? 'all' : limit}
                onChange={e => { const v = e.target.value; setLimit(v === 'all' ? 100000 : parseInt(v, 10)); setPage(1); }}
              >
                <option value={20}>20</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
                <option value="all">All</option>
              </select>
            </div>
            <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1 || loading}>← Prev</button>
            <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>Page {page} of {totalPages}</span>
            <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages || loading}>Next →</button>
          </div>
        )}
      </div>

      {showAdd && <AddProductModal categories={categories} onClose={() => setShowAdd(false)} />}
      {showImport && <ImportModal onClose={() => setShowImport(false)} />}
      {showExport && <ExportModal onClose={() => setShowExport(false)} />}
      {editTarget && <EditProductModal product={editTarget} categories={categories} onClose={() => setEditTarget(null)} />}
      {custTarget && <ManageCustomizationsModal product={custTarget} onClose={() => setCustTarget(null)} />}
      {showCategories && (
        <ManageCategoriesModal
          categories={categories}
          onClose={() => setShowCategories(false)}
          onSaved={() => productsApi.getCategories().then(res => setCategories(res.data || [])).catch(() => {})}
        />
      )}
    </div>
  );
}
